import type { VNode } from 'vue'

import type { Currency } from '@backmarket/http-api/src/standards/Currency'
import type { Bcp47Locale } from '@backmarket/http-api/src/standards/Locale'
import { isTestEnv } from '@backmarket/utils/env/isTestEnv'
import { IntlMessageFormat } from 'intl-messageformat'

import { CLIENT_GLOBAL_KEY, PROCESS_KEY } from '../../constants'
import type { I18nDefinition, I18nOptions, I18nValues } from '../../types/i18n'

interface Arguments {
  locale: Bcp47Locale
  showTranslationKeys?: boolean
  showDefaultMessage?: boolean
  currencyCode: Currency
  shouldLogMissingTranslationValue?: boolean
}

function isI18nOptions(obj: unknown): obj is I18nOptions {
  return !!obj && typeof obj === 'object' && 'logMissingTranslation' in obj
}

export default function createMessageFormatter({
  locale,
  showTranslationKeys = false,
  showDefaultMessage = false,
  currencyCode,
  shouldLogMissingTranslationValue = true,
}: Arguments) {
  return <Message extends string>(
    definition: I18nDefinition<Message>,
    valuesOrOptions?: I18nValues<Message> | I18nOptions,
    optionsIfValues?: I18nOptions,
  ): string | null => {
    // Determine if the second argument is values or options
    let values: I18nValues<Message> | undefined
    let options: I18nOptions | undefined

    if (valuesOrOptions) {
      if (isI18nOptions(valuesOrOptions)) {
        options = valuesOrOptions
      } else {
        values = valuesOrOptions as I18nValues<Message>
        options = optionsIfValues
      }
    }
    /**
     * In some cases, when definitions are computed from dynamic values,
     * we may end up in situations where nothing is passed to this function.
     */
    if (!definition) {
      // eslint-disable-next-line no-console
      console.error('[nuxt-module-i18n] Missing translation definition', {
        locale,
      })

      /**
       * Instead of crashing the application, let's gracefully handle the error
       * by displaying nothing to our end customers.
       */
      return null
    }

    if (showTranslationKeys) {
      return definition.id
    }

    try {
      if (
        process.server &&
        // Intl cache exists for this definition
        globalThis[PROCESS_KEY]?.translationsByLocaleCache[locale]?.messages[
          definition.id
        ] &&
        // Raw definition is not an empty string (not checking this in the cache as an empty string parses to a truthy AST value)
        globalThis[PROCESS_KEY]?.translationsByLocale[locale]?.[
          definition.id
        ] &&
        // Intl doesn't support forcing default message
        !showDefaultMessage
      ) {
        const intl = globalThis[PROCESS_KEY]?.translationsByLocaleCache[locale]

        return intl?.formatMessage(definition, values) as string
      }

      const translation = process.server
        ? globalThis[PROCESS_KEY]?.translationsByLocale[locale]?.[definition.id]
        : window[CLIENT_GLOBAL_KEY]?.translations?.[definition.id]

      /**
       * Do not display those warning in unit tests
       * as we don't have translations there.
       */
      if (!showDefaultMessage && !translation && !isTestEnv()) {
        if (translation === undefined) {
          // eslint-disable-next-line no-console
          console.error('[nuxt-module-i18n] Missing translation key', {
            definition,
            locale,
          })
        } else if (
          translation === '' &&
          (options?.logMissingTranslation ?? shouldLogMissingTranslationValue)
        ) {
          // eslint-disable-next-line no-console
          console.error('[nuxt-module-i18n] Missing translation value', {
            definition,
            locale,
          })
        }
      }

      const raw =
        !showDefaultMessage && translation
          ? translation
          : definition.defaultMessage

      const escaped = raw.replace(/{(\w*), html}/g, "'{$1}'")

      const message = new IntlMessageFormat(escaped, locale, {
        number: {
          currency: {
            style: 'currency',
            currency: currencyCode,
          },
        },
      })

      /**
       * Specifying VNode was necessary: message.format accepts a record object where values are of type Primitive | T | FormatXmlElement
       * T is inferred as string but it should be inferred as VNode. If T is inferred as string, we get a type error as VNode does not match Primitive | string | FormatXmlElement
       * */
      return message.format<VNode>(values) as string
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[nuxt-module-i18n] - createMessageFormatter', {
        definition,
        locale,
        error,
      })

      // If the key does not have a translation nor a default message.
      return null
    }
  }
}
